import { useCallback, useMemo, useState } from 'react'

import { getFractionSimplifySelectQuestionData, ProperFractionModel } from './FractionSimplifySelect.utils'

interface UseFractionSimplifySelectReturnType {
  question?: ProperFractionModel
  options?: ProperFractionModel[]
  formValid: boolean
  selectedAnswer?: number
  handleCheckAnswer: () => void
  onInputChange: (val?: number) => void
  goToNextQuestion: () => void
}

interface UseProperFractionProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
}

function useFractionSimplifySelect({
  difficultyLevel,
  onAnswerStateChange,
  onNextQuestionClick,
}: UseProperFractionProps): UseFractionSimplifySelectReturnType {
  // eslint-disable-next-line
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const {
    correctAnswerIndex = -1,
    question,
    fractions,
    maxVariation,
  } = useMemo(() => getFractionSimplifySelectQuestionData(difficultyLevel, taskHistory), [difficultyLevel, taskHistory])
  const [selectedAnswer, setSelectedAnswer] = useState<number>()

  const handleCheckAnswer = useCallback((): void => {
    if (selectedAnswer === undefined) return
    onAnswerStateChange(selectedAnswer === correctAnswerIndex)
  }, [selectedAnswer, onAnswerStateChange, correctAnswerIndex])

  const onInputChange = useCallback((val?: number) => setSelectedAnswer(val), [])

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) =>
      [`${question.numerator}-${question.denominator}`, ...prevState].slice(0, maxVariation * 0.7),
    )
    onInputChange()
    onNextQuestionClick()
  }, [onInputChange, onNextQuestionClick, maxVariation, question])

  return {
    question,
    options: fractions,
    formValid: selectedAnswer !== undefined,
    selectedAnswer,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
  }
}

export default useFractionSimplifySelect
